import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

const currencyFormatter = new Intl.NumberFormat("el-GR", {
  style: "currency",
  currency: "EUR",
});

const formatCurrency = (value) => currencyFormatter.format(Number(value ?? 0));

const formatValue = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  return value;
};

const sumPaymentAmounts = (payments = []) =>
  payments.reduce((sum, payment) => sum + Number(payment?.amount ?? 0), 0);

const isHiddenMode = (value) =>
  value?.toString?.().trim?.().toLowerCase?.() === "hidden";

const isTrueFlag = (value) =>
  value === true || value?.toString?.().trim?.().toLowerCase?.() === "true";

const getPaymentTimingLabel = (paidOnIssue) =>
  isTrueFlag(paidOnIssue)
    ? "ΑΜΕΣΑ ΚΑΤΑ ΤΗΝ ΕΚΔΟΣΗ"
    : "ΜΕΤΑ ΤΗΝ ΕΚΔΟΣΗ";

const headerLayoutSx = {
  alignSelf: "center",
  display: "grid",
  gap: { xs: 1, md: 1.25, lg: 1.5 },
  gridTemplateColumns: {
    xs: "1fr",
    lg: "minmax(220px, 240px) minmax(380px, 1fr) minmax(240px, 340px)",
  },
  width: "100%",
};

const cardSurfaceSx = {
  backgroundClip: "border-box",
  backgroundColor: "#fff",
  border: "1px solid rgba(0, 0, 0, 0.125)",
  borderRadius: "0.25rem",
  height: "100%",
  minWidth: 0,
  overflow: "hidden",
  p: { xs: 1.25, md: 1.5 },
  position: "relative",
  wordWrap: "break-word",
};

const titleCardSx = {
  ...cardSurfaceSx,
  display: "flex",
  justifyContent: "flex-start",
};

const sectionSx = {
  backgroundColor: "#fff",
  minHeight: "100%",
  p: 1.5,
};

const sectionTitleSx = {
  color: "#111827",
  fontWeight: 900,
  letterSpacing: 0,
  lineHeight: 1.2,
  textTransform: "uppercase",
};

const statusColors = {
  saved: {
    backgroundColor: "#e8f0fe",
    borderColor: "#9bbcf4",
    color: "#123f7a",
  },
  submitted: {
    backgroundColor: "#fff6df",
    borderColor: "#e6ba55",
    color: "#7a4b00",
  },
  service_processing: {
    backgroundColor: "#fff6df",
    borderColor: "#e6ba55",
    color: "#7a4b00",
  },
  approved: {
    backgroundColor: "#e7f6ec",
    borderColor: "#82c89a",
    color: "#176234",
  },
  rejected: {
    backgroundColor: "#fdeaea",
    borderColor: "#e18a8a",
    color: "#9a1b1b",
  },
  cancelled: {
    backgroundColor: "#fdeaea",
    borderColor: "#e18a8a",
    color: "#9a1b1b",
  },
  partially_approved: {
    backgroundColor: "#eef2ff",
    borderColor: "#a5b4fc",
    color: "#3730a3",
  },
};

const getStatusSx = (statusDataKey) => {
  return (
    statusColors?.[statusDataKey] ?? {
      backgroundColor: "#f3f4f6",
      borderColor: "#d1d5db",
      color: "#374151",
    }
  );
};

const LabelValueRow = ({ label, value, hideWhenEmpty = false }) => {
  if (hideWhenEmpty && !value) return null;

  return (
    <Grid
      item
      xs={12}
      md={12}
      sx={{ mb: 0.5, px: { xs: 0, md: 0.75 } }}
    >
      <Box
        sx={{
          alignItems: "baseline",
          columnGap: 0.75,
          display: "flex",
          flexWrap: "wrap",
          minWidth: 0,
          rowGap: 0.25,
        }}
      >
        <Typography
          component="span"
          sx={{
            color: "#475467",
            flexShrink: 0,
            fontSize: 12.5,
            fontWeight: 500,
            lineHeight: 1.25,
            overflowWrap: "anywhere",
          }}
        >
          {label}:
        </Typography>
        <Box
          component="span"
          sx={{
            color: "#101828",
            fontSize: { xs: 13.25, md: 13.5 },
            fontWeight: 700,
            lineHeight: 1.25,
            minWidth: 0,
            overflowWrap: "anywhere",
          }}
        >
          {formatValue(value)}
        </Box>
      </Box>
    </Grid>
  );
};

const DocumentSummaryStrip = ({ transaction, reserveActionSpace = false }) => {
  const summaryItems = [
    transaction?.transTypeName,
    transaction?.transDateFormatted,
    transaction?.transNumber ? `#${transaction.transNumber}` : null,
  ].filter(Boolean);

  if (summaryItems.length === 0) return null;

  return (
    <Grid
      item
      xs={12}
      sx={{
        mb: 0.75,
        pl: { xs: 0, md: 0.75 },
        pr: { xs: 0, sm: reserveActionSpace ? 18 : 0 },
      }}
    >
      <Box
        sx={{
          alignItems: "baseline",
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 0.5, md: 1 },
          minWidth: 0,
        }}
      >
        {summaryItems.map((item, index) => (
          <Box
            key={`${item}-${index}`}
            sx={{
              alignItems: "baseline",
              display: "flex",
              gap: { xs: 0.5, md: 1 },
              minWidth: 0,
            }}
          >
            {index > 0 && (
              <Typography
                aria-hidden="true"
                sx={{
                  color: "#98a2b3",
                  fontSize: { xs: 14, md: 15 },
                  fontWeight: 600,
                  lineHeight: 1.2,
                }}
              >
                ·
              </Typography>
            )}
            <Typography
              component="span"
              sx={{
                color: "#101828",
                fontSize: { xs: 15, md: 16 },
                fontWeight: 800,
                letterSpacing: 0,
                lineHeight: 1.25,
                overflowWrap: "break-word",
              }}
            >
              {item}
            </Typography>
          </Box>
        ))}
      </Box>
    </Grid>
  );
};

const TotalRow = ({ label, value, tone = "default" }) => {
  const isApproved = tone === "approved";
  const isPrimary = tone === "primary";
  const isPayment = tone === "payment";
  const isEmphasized = isPrimary || isApproved || isPayment;

  return (
    <Box
      sx={{
        alignItems: "baseline",
        borderTop:
          isPrimary || isApproved ? "1px solid rgba(17, 24, 39, 0.16)" : "none",
        display: "grid",
        gap: 1,
        gridTemplateColumns: "minmax(80px, 1fr) minmax(112px, auto)",
        py: isEmphasized ? 0.45 : 0.25,
      }}
    >
      <Typography
        sx={{
          color: isApproved ? "#176234" : "#111827",
          fontSize: isEmphasized ? 13 : 12.5,
          fontWeight: isEmphasized ? 900 : 800,
          lineHeight: 1.25,
          textAlign: "right",
        }}
      >
        {label}:
      </Typography>
      <Typography
        sx={{
          color: isApproved ? "#176234" : "#101828",
          fontSize: isPrimary || isApproved ? 15 : isPayment ? 14 : 13,
          fontWeight: isEmphasized ? 900 : 700,
          lineHeight: 1.25,
          textAlign: "right",
          whiteSpace: "nowrap",
        }}
      >
        {formatCurrency(value)}
      </Typography>
    </Box>
  );
};

const TotalsCalculation = ({ transaction, showApprovedAmount }) => {
  const paidAmount =
    transaction?.paidAmount ?? sumPaymentAmounts(transaction?.payments);

  return (
    <Box
      sx={{
        ...cardSurfaceSx,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        justifyContent: { xs: "flex-start", lg: "space-between" },
      }}
    >
      <Box>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          sx={{ mb: transaction?.statusComments ? 0.5 : 0 }}
        >
          <Typography
            component="span"
            sx={{
              color: "#111827",
              fontSize: 12.5,
              fontWeight: 800,
              lineHeight: 1.25,
            }}
          >
            Κατάσταση:
          </Typography>
          <StatusValue transaction={transaction} />
        </Stack>
        {transaction?.statusComments && (
          <Typography
            sx={{
              color: "#667085",
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.35,
              overflowWrap: "anywhere",
            }}
          >
            {transaction.statusComments}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          backgroundColor: "#fafafa",
          border: "1px solid rgba(17, 24, 39, 0.12)",
          borderRadius: "0.25rem",
          p: 1,
          width: "100%",
        }}
      >
        <TotalRow label="Αξία" value={transaction?.netValue} />
        <TotalRow label="ΦΠΑ" value={transaction?.vatValue} />
        <TotalRow label="Σύνολο" value={transaction?.totalAmount} tone="primary" />
        <TotalRow
          label="Σύνολο πληρωμών"
          value={paidAmount}
          tone="payment"
        />
        {showApprovedAmount && (
          <TotalRow
            label="Εγκεκριμένο"
            value={transaction?.approvedAmount}
            tone="approved"
          />
        )}
      </Box>
    </Box>
  );
};

const StatusValue = ({ transaction }) => {
  const statusSx = getStatusSx(transaction?.statusDataKey);

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Chip
        size="small"
        label={formatValue(transaction?.statusAbbr ?? transaction?.statusName)}
        sx={{
          ...statusSx,
          border: `1px solid ${statusSx.borderColor}`,
          borderRadius: "0.25rem",
          fontSize: 12,
          fontWeight: 900,
          height: 24,
        }}
      />
      <Typography
        component="span"
        sx={{
          color: "#667085",
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.35,
        }}
      >
        {transaction?.statusDateFormatted}
      </Typography>
    </Stack>
  );
};

export const ExpensePaymentsPanel = ({ transaction, onPaymentAction }) => {
  const payments = transaction?.payments ?? [];
  const canAddPayment =
    transaction?.settings?.addPayment?.can === true ||
    transaction?.canAddPayment === true;
  const paymentGridTemplateColumns = "minmax(0, 1fr) 112px";

  return (
    <Box sx={sectionSx}>
      <Stack spacing={1}>
        <Box sx={{ overflowX: "auto" }}>
          <Stack spacing={0.25} sx={{ minHeight: 78, minWidth: 300 }}>
            <Box
              sx={{
                alignItems: "baseline",
                display: "grid",
                gap: 1,
                gridTemplateColumns: paymentGridTemplateColumns,
              }}
            >
              <Typography sx={sectionTitleSx} variant="h4">
                Πληρωμές
              </Typography>
              <Typography
                sx={{
                  color: "#101828",
                  fontSize: 14,
                  fontWeight: 900,
                  textAlign: "right",
                  whiteSpace: "nowrap",
                }}
              >
                {formatCurrency(transaction?.paidAmount)}
              </Typography>
            </Box>

            {payments.length === 0 && (
              <Typography sx={{ color: "#667085", fontSize: 13 }}>
                Δεν έχουν καταχωρηθεί πληρωμές.
              </Typography>
            )}

            {payments.map((payment) => (
              <Box
                key={payment.paymentTransHeaderId}
                sx={{
                  alignItems: "center",
                  borderBottom: "1px solid rgba(17, 24, 39, 0.08)",
                  display: "grid",
                  gap: 1,
                  gridTemplateColumns: paymentGridTemplateColumns,
                  py: 0.35,
                }}
              >
                <Box
                  sx={{
                    alignItems: "center",
                    display: "grid",
                    gap: 0.5,
                    gridTemplateColumns:
                      payment.canEdit === true || payment.canDelete === true
                        ? "minmax(0, 1fr) auto"
                        : "minmax(0, 1fr)",
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#101828",
                      fontSize: 13,
                      fontWeight: 700,
                      lineHeight: 1.25,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={`${formatValue(payment.paymentNumber)} - ${formatValue(
                      payment.paymentDateFormatted,
                    )}`}
                  >
                    {formatValue(payment.paymentNumber)} -{" "}
                    {formatValue(payment.paymentDateFormatted)}
                  </Typography>
                  {(payment.canEdit === true || payment.canDelete === true) && (
                    <Stack direction="row" spacing={0.25}>
                      {payment.canEdit === true && (
                        <Tooltip
                          title="Επεξεργασία πληρωμής"
                          disableFocusListener
                        >
                          <IconButton
                            aria-label="Επεξεργασία πληρωμής"
                            size="small"
                            color="primary"
                            onClick={() =>
                              onPaymentAction?.("editPayment", payment)
                            }
                            sx={{ height: 28, width: 28 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {payment.canDelete === true && (
                        <Tooltip title="Διαγραφή πληρωμής" disableFocusListener>
                          <IconButton
                            aria-label="Διαγραφή πληρωμής"
                            size="small"
                            color="error"
                            onClick={() =>
                              onPaymentAction?.("deletePayment", payment)
                            }
                            sx={{ height: 28, width: 28 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  )}
                </Box>
                <Typography
                  sx={{
                    color: "#101828",
                    fontSize: 13,
                    fontWeight: 800,
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatCurrency(payment.amount)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {canAddPayment && (
          <Button
            size="small"
            variant="outlined"
            startIcon={"€"}
            onClick={() => onPaymentAction?.("addPayment")}
            title={`${transaction?.settings?.addPayment?.txt ?? "Προσθήκη Πληρωμής"
              } (Alt+P)`}
            sx={{ alignSelf: "flex-start", fontWeight: 800 }}
          >
            {transaction?.settings?.addPayment?.txt ?? "Προσθήκη Πληρωμής"}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

const ExpenseTransactionHeader = ({
  transaction,
  modeConfig = {},
  actionsDisabled = false,
  onHeaderAction,
}) => {
  const supplier = [transaction?.supplierAfm, transaction?.supplierName]
    .filter(Boolean)
    .join(" - ");
  const showSupplier = !isHiddenMode(modeConfig.supplierMode);
  const editHeaderSetting = transaction?.settings?.editHeader;
  const canEditHeader = editHeaderSetting?.can === true;
  const editHeaderText =
    editHeaderSetting?.txt ?? "Επεξεργασία Στοιχείων";
  const showApprovedAmount =
    transaction?.settings?.showApprovedAmount === true ||
    transaction?.showApprovedAmount === true;

  return (
    <Box sx={headerLayoutSx}>
      <Box sx={titleCardSx}>
        <Typography
          component="h2"
          sx={{
            color: "#111827",
            fontSize: { xs: 21, md: 22, lg: 24 },
            fontWeight: 800,
            lineHeight: 1.15,
            maxWidth: "100%",
            overflowWrap: "break-word",
            wordBreak: "normal",
          }}
        >
          {formatValue(transaction?.mhdName)}
        </Typography>
      </Box>
      <Grid sx={cardSurfaceSx} container>
        {canEditHeader && (
          <Box
            sx={{
              flexBasis: "100%",
              mb: { xs: 1, sm: 0 },
              position: { xs: "static", sm: "absolute" },
              right: { sm: 12 },
              textAlign: "right",
              top: { sm: 12 },
              zIndex: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon fontSize="small" />}
              disabled={actionsDisabled}
              onClick={() => onHeaderAction?.("editHeader", editHeaderSetting)}
              sx={{
                fontWeight: 700,
                minHeight: 28,
                py: 0.25,
                whiteSpace: "nowrap",
              }}
            >
              {editHeaderText}
            </Button>
          </Box>
        )}
        <DocumentSummaryStrip
          transaction={transaction}
          reserveActionSpace={canEditHeader}
        />
        {showSupplier && (
          <LabelValueRow label="Προμηθευτής" value={supplier} />
        )}
        <LabelValueRow label="Αιτιολογία" value={transaction?.description} />
        <LabelValueRow
          label="Εξόφληση"
          value={getPaymentTimingLabel(transaction?.paidOnIssue)}
        />
        <LabelValueRow
          label="Σχόλια"
          value={transaction?.comments}
          hideWhenEmpty
        />
        <LabelValueRow
          label="Παρατηρήσεις"
          value={transaction?.remarks}
          hideWhenEmpty
        />

      </Grid>
      <TotalsCalculation
        transaction={transaction}
        showApprovedAmount={showApprovedAmount}
      />
    </Box>
  );
};

export default ExpenseTransactionHeader;
